
export const deg2rad = d => d * Math.PI / 180;
export const rad2deg = r => r * 180 / Math.PI;



// calculate sideral time
function localSiderealTime(date, lon) {
    const JD = (date.getTime() / 86400000.0) + 2440587.5;
    const D = JD - 2451545.0;
    let GMST = 280.46061837 + 360.98564736629 * D;
    GMST = (GMST % 360 + 360) % 360; // normalizza [0,360)
    return deg2rad(GMST) + lon;
}

const DEFAULT_SETTINGS = {
    raProp: 'ra',
    decProp: 'dec'
}

/**
 * Filters the visible objects for an indicated location
 * @param {Object[]} objects need to have ra and dec
 * @param {*} lat
 * @param {*} lon
 * @param {Date} date the current time, time where to find things
 * @param {Number} horizon Virtual horizon height in decimal degrees
 * @param {object} settings
 * @returns
 */
export function filterVisibleObjects(objects, lat, lon, date = new Date(), horizon = 0, settings = DEFAULT_SETTINGS) {
  lat = deg2rad(lat);
  lon = deg2rad(lon);
  const lst = localSiderealTime(date, lon);

  return objects
    .map(obj => {
      const ra = deg2rad(obj[settings.raProp]);   // in gradi input
      const dec = deg2rad(obj[settings.decProp]); // in gradi input

      // Angolo orario
      let H = lst - ra;
      H = ((H + Math.PI) % (2 * Math.PI)) - Math.PI; // normalizza [-π,π]

      // Altezza
      const sinAlt = Math.sin(dec) * Math.sin(lat) +
                     Math.cos(dec) * Math.cos(lat) * Math.cos(H);
      const alt = Math.asin(sinAlt);

      // Azimut
      const y = -Math.sin(H);
      const x = Math.cos(dec) * Math.sin(lat) -
                Math.sin(dec) * Math.cos(lat) * Math.cos(H);
      let az = Math.atan2(y, x);
      az = (az % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI); // [0,2π)

      return {
        ...obj,
        alt: rad2deg(alt),
        az: rad2deg(az)
      };
    })
    .filter(obj => obj.alt >= horizon);
}
export function hour2degree(ra) {
  return ra > 12 ? (ra - 24) * 15 : ra * 15;
}


/**
 * Draw points of virtual horizon.
 * @param {number} latitude latitude of observer in decimal degrees.
 * @param {number} longitude longitude of observer in decimal degrees.
 * @param {number} radiusKm radius of the horizon in kilometers (default 500km, which is about 20° at sea level).
 * @param {number} [steps=360] number of points to generate along the horizon line (default 360, i.e. one point per degree).
 * @returns {object} Un oggetto GeoJSON MultiLineString che rappresenta l'orizzonte.
 */
export function getVirtualHorizon(latitude, longitude, radiusKm = 500, steps = 360) {
  // Raggio della Terra in chilometri
  const EARTH_RADIUS_KM = 6371;
  const coordinates = [];

  for (let i = 0; i <= steps; i++) {
    const angle = (i * 360) / steps;
    const bearingRad = (angle * Math.PI) / 180;

    // Calcolo la nuova latitudine
    const latRad = (latitude * Math.PI) / 180;
    const newLatRad = Math.asin(
      Math.sin(latRad) * Math.cos(radiusKm / EARTH_RADIUS_KM) +
      Math.cos(latRad) * Math.sin(radiusKm / EARTH_RADIUS_KM) * Math.cos(bearingRad)
    );

    // Calcolo la nuova longitudine
    const lonRad = (longitude * Math.PI) / 180;
    let newLonRad = lonRad + Math.atan2(
      Math.sin(bearingRad) * Math.sin(radiusKm / EARTH_RADIUS_KM) * Math.cos(latRad),
      Math.cos(radiusKm / EARTH_RADIUS_KM) - Math.sin(latRad) * Math.sin(newLatRad)
    );

    // Converto da radianti a gradi e aggiungo al risultato
    const newLat = (newLatRad * 180) / Math.PI;
    const newLon = (newLonRad * 180) / Math.PI;

    coordinates.push([newLon, newLat]);
  }

  return {
    "type": "FeatureCollection",
    "features": [{
      "type": "Feature",
      "properties": {
        "n": "Orizzonte virtuale"
      },
      "geometry": {
        "type": "MultiLineString",
        "coordinates": [coordinates]
      }
    }]
  };
}


/**
 * calculate the virtual horizon line given observer's position, time and horizon height in degrees.
 * @param {number} latitude - latitude of the observer in decimal degrees.
 * @param {number} longitude - longitude of the observer in decimal degrees.
 * @param {Date} date - current date and time, used to calculate the local sidereal time.
 * @param {number} horizonAlt - height of the horizon in decimal degrees (default 0, i.e. true horizon; positive values for elevated horizons, negative for underground).
 * @param {number} [steps=360] - number of points to generate along the horizon line (default 360, i.e. one point per degree).
 * @returns {object} a GeoJSON FeatureCollection with a MultiLineString geometry representing the virtual horizon line, where each point has properties "n" (name) and "altitudine" (the horizon altitude in degrees).
 */
export function getVirtualHorizonByAltitude(latitude, longitude, date, horizonAlt = 0, steps = 360) {
  const deg2rad = (deg) => deg * Math.PI / 180;
  const rad2deg = (rad) => rad * 180 / Math.PI;

  const lat = deg2rad(latitude);
  const lon = deg2rad(longitude);
  const alt = deg2rad(horizonAlt);

  // Sideral local time calculation (LST) - copied
  const J2000 = new Date('2000-01-01T12:00:00Z');
  const jd = (date.getTime() - J2000.getTime()) / 86400000 + 2451545.0;
  const T = (jd - 2451545.0) / 36525;
  const theta0 = (280.46061837 + 360.98564736629 * (jd - 2451545.0) + 0.000387933 * T * T - T * T * T / 38710000) % 360;
  const lst = deg2rad(theta0) + lon;

  const coordinates = [];
  for (let i = 0; i <= steps; i++) {
    const az = deg2rad(i); // Azimut in rad [0, 2π)

    // Dec calculation from height and azimut
    const sinDec = Math.sin(lat) * Math.sin(alt) + Math.cos(lat) * Math.cos(alt) * Math.cos(az);
    const dec = Math.asin(sinDec);

    // hour angle
    const cosH = (Math.sin(alt) - Math.sin(lat) * sinDec) / (Math.cos(lat) * Math.cos(dec));
    const H = Math.atan2(-Math.sin(az) * Math.cos(alt), cosH);

    // RA calculation
    const ra = (lst - H) % (2 * Math.PI);

    // Conversion in degrees
    const newLon = rad2deg(ra); // RA è la Longitudine nel sistema equatoriale
    const newLat = rad2deg(dec); // Dec è la Latitudine nel sistema equatoriale
    coordinates.push([newLon, newLat]);
  }

  return {
    "type": "FeatureCollection",
    "features": [{
      "type": "Feature",
      "properties": {
        "n": "Orizzonte virtuale",
        "altitudine": horizonAlt
      },
      "geometry": {
        "type": "MultiLineString",
        "coordinates": [coordinates]
      }
    }]
  };
}


function filterMPCFeatureCollection(features, lat, lon, date = new Date(), horizon = 20) {
  const deg2rad = d => d * Math.PI / 180;
  const rad2deg = r => r * 180 / Math.PI;

  const latRad = deg2rad(lat);
  const lonRad = deg2rad(lon);

  function localSiderealTime(date, lonRad) {
    const JD = (date.getTime() / 86400000.0) + 2440587.5;
    const D = JD - 2451545.0;
    // Greenwich Mean Sidereal Time (GMST)
    let gmst = 280.46061837 + 360.98564736629 * D;
    gmst = (gmst % 360 + 360) % 360;
    return deg2rad(gmst) + lonRad;
  }

  const lst = localSiderealTime(date, lonRad);

  const visibleFeatures = features
    .map(f => {
      const d = f?.properties?.itemData ?? {};
      if (!d["R.A."] || !d["Decl."]) return null;

      // The MPC data provides R.A. in hours, not degrees.  The
      // geometry stored elsewhere has already converted it using
      // `hour2degree`.  When filtering we must do the same so that
      // our altitude/azimuth math is sane.
      const raHours = d["R.A."];
      const ra = deg2rad(hour2degree(raHours));   // convert to degrees then radians
      const dec = deg2rad(d["Decl."]); // Declination is already in degrees

      // Hour angle
      let H = lst - ra;
      H = ((H + Math.PI) % (2 * Math.PI)) - Math.PI;

      // Altitude
      const sinAlt = Math.sin(dec) * Math.sin(latRad) +
                     Math.cos(dec) * Math.cos(latRad) * Math.cos(H);
      const alt = Math.asin(sinAlt);

      // Azimuth
      const y = -Math.sin(H);
      const x = Math.cos(dec) * Math.sin(latRad) -
                Math.sin(dec) * Math.cos(latRad) * Math.cos(H);
      let az = Math.atan2(y, x);
      az = (az % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);

      // clone the feature and add alt/az to properties
      return {
        ...f,
        properties: {
          ...f.properties,
          alt: rad2deg(alt),
          az: rad2deg(az)
        }
      };
    })
    .filter(f => f && f.properties.alt >= horizon);

  return visibleFeatures;
}


/**
 *
 * @param {FeatureCollection} asteroids feature collection of asteroids in GeoJSON format
 * @param {Object} filter contains:
 * - horizon: boolean, if true, filter by horizon
 * @param {*} filterData contains:
 * - horizonData: GeoJSON FeatureCollection of the horizon
 * - position: {latitude, longitude} of the observer
 * - time: Date object of the observation time
 * - activeHorizon: boolean, if true, the horizon is active
 * - horizonHeight: number, height of the horizon in degrees
 * @returns
 */
export function applyAsteroidsFilter(asteroids, filter, filterData) {
  if (!asteroids || !filter) return asteroids;

  // support passing either a feature array or an object with .features
  let originalIsCollection = false;
  let features = [];
  if (Array.isArray(asteroids)) {
    features = asteroids;
  } else if (asteroids.features) {
    originalIsCollection = true;
    features = asteroids.features;
  } else {
    return asteroids; // unknown shape
  }

  // helper to parse numeric value or undefined
  const toNum = v => {
    if (v == null || v === '') return undefined;
    const n = parseFloat(v);
    return isNaN(n) ? undefined : n;
  };
  const inRange = (val, min, max) => {
    if (min !== undefined && val < min) return false;
    if (max !== undefined && val > max) return false;
    return true;
  };

  // horizon filter uses special geometry logic
  if (filter.horizon && filterData?.horizonData) {
    const { position, time, horizonHeight } = filterData;
      features = filterMPCFeatureCollection(
        features,
        position.latitude,
        position.longitude,
        time,
        horizonHeight
      );
  }

  // text match on Temp_Desig (case-insensitive substring)
  if (filter.Temp_Desig) {
    const needle = filter.Temp_Desig.toString().toLowerCase();
    features = features.filter(f =>
      f.properties?.Temp_Desig?.toString().toLowerCase().includes(needle)
    );
  }
  const tempDesig = filter.tempDesig;
  // numeric ranges: score, coordinates, speed, nobs, not seen
  const scoreMin = toNum(filter.scoreMin);
  const scoreMax = toNum(filter.scoreMax);
  const raMin = toNum(filter.raMin);
  const raMax = toNum(filter.raMax);
  const declMin = toNum(filter.declMin);
  const declMax = toNum(filter.declMax);
  const motionNA = filter.motionNA;
  const speedMin = toNum(filter.speedMin);
  const speedMax = toNum(filter.speedMax);
  const nobsMin = toNum(filter.nobsMin);
  const nobsMax = toNum(filter.nobsMax);
  const notSeenMin = toNum(filter.notSeenMin);
  const notSeenMax = toNum(filter.notSeenMax);
  const vMin = toNum(filter.vMin);
  const vMax = toNum(filter.vMax);
  const hMin = toNum(filter.hMin);
  const hMax = toNum(filter.hMax);
  const noteB = filter.noteB;
  const noteS = filter.noteS;
  const notePCCP = filter.notePCCP;
  const noteUndefined = filter.noteUndefined;

  // helper to compute average speed given ephemerides object
  const averageSpeed = (id) => {
    if (!filterData?.ephemerids) return undefined;
    const entry = filterData.ephemerids[id];
    const arr = entry?.ephem?.map(e => e.motion).filter(v => v != null);
    if (!arr || arr.length === 0) return undefined;
    const sum = arr.reduce((a, v) => a + v, 0);
    return sum / arr.length;
  };

  if (
    tempDesig !== undefined ||
    scoreMin !== undefined ||
    scoreMax !== undefined ||
    raMin !== undefined ||
    raMax !== undefined ||
    declMin !== undefined ||
    declMax !== undefined ||
    speedMin !== undefined ||
    speedMax !== undefined ||
    nobsMin !== undefined ||
    nobsMax !== undefined ||
    notSeenMin !== undefined ||
    notSeenMax !== undefined ||
    vMin !== undefined ||
    vMax !== undefined ||
    hMin !== undefined ||
    hMax !== undefined ||
    noteB !== undefined ||
    noteS !== undefined ||
    notePCCP !== undefined ||
    noteUndefined !== undefined ||

    motionNA !== undefined
  ) {
    features = features.filter(f => {
      const p = f.properties || {};
      if(tempDesig !== undefined) {
        if (!p.Temp_Desig || !p.Temp_Desig.toString().toLowerCase().includes(tempDesig.toString().toLowerCase())) {
          return false;
        }
      }
      if (scoreMin !== undefined || scoreMax !== undefined) {
        if (!inRange(p.Score, scoreMin, scoreMax)) return false;
      }
      if (raMin !== undefined || raMax !== undefined) {
        if (!inRange(p['R.A.'], raMin, raMax)) return false;
      }
      if (declMin !== undefined || declMax !== undefined) {
        if (!inRange(p['Decl.'], declMin, declMax)) return false;
      }
      if (nobsMin !== undefined || nobsMax !== undefined) {
        if (!inRange(p.NObs, nobsMin, nobsMax)) return false;
      }
      if (notSeenMin !== undefined || notSeenMax !== undefined) {
        if (!inRange(p.Not_Seen_dys, notSeenMin, notSeenMax)) return false;
      }
      if (speedMin !== undefined || speedMax !== undefined) {
        const avg = averageSpeed(p.Temp_Desig);
        if( (motionNA !== false && avg === undefined)
            || (motionNA !== false && isNaN(avg))) {
          return true; // if motionNA is checked, include objects with no speed data
        }
        if (avg === undefined) return false;
        if (!inRange(avg, speedMin, speedMax)) return false;
      }
      if (vMin !== undefined || vMax !== undefined) {
        if (!inRange(p.V, vMin, vMax)) return false;
      }
      if (hMin !== undefined || hMax !== undefined) {
        if (!inRange(p.H, hMin, hMax)) return false;
      }
      const hasNoteFilters =
        noteB !== undefined ||
        noteS !== undefined ||
        notePCCP !== undefined ||
        noteUndefined !== undefined;
      if (hasNoteFilters) {
        const note = p.Note;
        if (note === "B") return noteB ?? true;
        if (note === "S") return noteS ?? true;
        if (note === "PCCP") return notePCCP ?? true;
        if (!note) return noteUndefined ?? true;
      }



      return true;
    });
  }

  if (originalIsCollection) {
    return {
      ...asteroids,
      features
    };
  }
  return features;
}


export function zenithRADec(latDeg, lonDeg, date = new Date()) {

  const norm360 = d => ((d % 360) + 360) % 360;

  // Julian Day (UTC)
  const JD = date.getTime() / 86400000 + 2440587.5;
  const D  = JD - 2451545.0;

  // GMST in deg (Compact formula)
  let GMST = 280.46061837 + 360.98564736629 * D;
  GMST = norm360(GMST);

  //  EAST positive lng
  const LSTdeg = norm360(GMST + lonDeg);

  const RA_zen_deg  = LSTdeg;
  const Dec_zen_deg = latDeg;

  return {
    ra_deg: RA_zen_deg,           // 0..360
    ra_hours: RA_zen_deg / 15,    // 0..24
    dec_deg: Dec_zen_deg
  };
}

/**
 * Returns the center of the map given your latitude, longitude and date.
 * This will put the azimuth at the center of the map.
 * @param {number} latDeg latitude
 * @param {number} lonDeg longitude
 * @param {Date} date the date
 * @returns {number[]}
 */
export function skyMapCenter(latDeg, lonDeg, date = new Date()) {

  const norm360 = d => ((d % 360) + 360) % 360;

  // Julian Day (UTC)
  const JD = date.getTime() / 86400000 + 2440587.5;
  const D  = JD - 2451545.0;

  // GMST in gradi
  let GMST = 280.46061837 + 360.98564736629 * D;
  GMST = norm360(GMST);

  // LST (longitudine Est positiva!)
  const LSTdeg = norm360(GMST + lonDeg);

  return [
    LSTdeg,   // longitude = RA (deg)
    latDeg,   // latitude  = Dec
    0
  ];
}

/**
 *
 * @param {string} line the line of text to parse
 * @returns {obj} and object structured like this:
 * date: dateUtc,
 * ra: "22 41 32.2"
 * dec: "+01 51 58"
 * elong: 164.6
 * v:  22.6
 * motion:   speed arcsecs/min // TODO: see also 2 directions.
 * pa:        // Position Angle, deg
 */
export function parseEphemLine(line) {
  line = line.trim();
  if (!line || line.startsWith("Date") || line.startsWith("h")) {
    return null; // ignora header o righe vuote
  }

  // split su 2 o più spazi
  const parts = line.split(/\s{1,}/);

  // Date + ora UT
  const year = parts[0];
  const month = parts[1]; // JS usa 0-based
  const day = parts[2]; //
  const [hour, min] = parts[3].length === 2 // can be 01 or 0130
    ? [parts[3], "00"]
    : [parts[3].substring(0,2), parts[3].substring(2,4)]
  // 2025-09-06T09:10:00Z
  const dateString = `${year}-${month}-${day}T${hour}:${min}:00Z`;
  const dateUtc = new Date(dateString);

  // Extract Map and Offsets hrefs from <a href="...">Map</a>/<a href="...">Offsets</a>
  const hrefs = [...line.matchAll(/<a href="([^"]+)"/g)].map(m => m[1]);
  const toAbsolute = href => href.startsWith('http') ? href : `https://www.minorplanetcenter.net${href}`;
  const mapUrl = hrefs[0] ? toAbsolute(hrefs[0]) : null;
  const offsetsUrl = hrefs[1] ? toAbsolute(hrefs[1]) : (hrefs[0] ? toAbsolute(hrefs[0]) : null);

  return {
    date: dateUtc,          // YYYY MM
    ra: `${parts[4]} ${parts[5]} ${parts[6]}`,    // "22 41 32.2"
    dec: `${parts[7]} ${parts[8]} ${parts[9]}`,   // "+01 51 58"
    // ra in decimal degrees: (22 + 41/60 + 32.2/3600) * 15
    radd: (parseFloat(parts[4]) + parseFloat(parts[5])/60 + parseFloat(parts[6])/3600) * 15,
    decdd: parseFloat(parts[7]) + parseFloat(parts[8])/60 + parseFloat(parts[9])/3600, // in decimal degrees, with sign
    elong: parseFloat(parts[10]),     // es. 164.6
    v: parseFloat(parts[11]),         // es. 22.6
    motion: parseFloat(parts[12]),    // speed arcsecs/min
    pa: parseFloat(parts[13]),        // Position Angle, deg
    mapUrl,
    offsetsUrl
  };
}

/**
 * Extract data from ephemerides HTML response.
 * @param {string} html - Html to parse
 * @returns {Object} an object that contains a key for each data collected.
 */
export function parseEphemeridesHtml(html) {
  const results = {};
  // Trova tutti i blocchi <b>ObjName</b> ... <pre>...</pre>
  const objRegex = /<b>([A-Za-z0-9]+)<\/b>[\s\S]*?<pre>([\s\S]*?)<\/pre>/g;
  let match;
  while ((match = objRegex.exec(html)) !== null) {
    const obj = match[1];
    const pre = match[2];
    // Estrai le righe della tabella (ignora header e link)
    const lines = pre
      .split('\n')
      .map(l => l.trim())
      .filter(l => l && !l.startsWith('Date') && !l.startsWith('h') && !l.startsWith('<a') && !l.startsWith('Motion') && !l.startsWith('Uncertainty'));

    const suppressedCount = lines.filter(line => line?.indexOf("suppressed") > 0).length;
    const ephem = lines
      .filter(line => !(line?.indexOf("suppressed") > 0))
      .map(line => parseEphemLine(line))
      .filter(Boolean);

    results[obj] = { ephem, suppressedCount };
  }
  return results;
}